import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { getApiErrorMessage } from '../services/apiService';

interface AddNaptarBejegyzesDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (payload: { datum: string; kezdesIdo: string; befejezesIdo: string; leiras: string }) => Promise<void>;
  defaultStartTime?: string;
  defaultEndTime?: string;
}

const AddNaptarBejegyzesDialog: React.FC<AddNaptarBejegyzesDialogProps> = ({
  open,
  title,
  onClose,
  onSubmit,
  defaultStartTime,
  defaultEndTime,
}) => {
  const [datum, setDatum] = useState<Date | null>(new Date());
  const [kezdesIdo, setKezdesIdo] = useState(defaultStartTime || '');
  const [befejezesIdo, setBefejezesIdo] = useState(defaultEndTime || '');
  const [leiras, setLeiras] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setDatum(new Date());
    setKezdesIdo(defaultStartTime || '');
    setBefejezesIdo(defaultEndTime || '');
    setLeiras('');
    setError(null);
  }, [open, defaultStartTime, defaultEndTime]);

  const toLocalDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T12:00:00`;
  };

  const handleSave = async () => {
    if (!datum) {
      setError('A dátum megadása kötelező.');
      return;
    }

    if (!leiras.trim()) {
      setError('A leírás megadása kötelező.');
      return;
    }

    if (!kezdesIdo || !befejezesIdo) {
      setError('A kezdési és befejezési idő megadása kötelező.');
      return;
    }

    if (kezdesIdo >= befejezesIdo) {
      setError('A kezdési időnek korábbinak kell lennie, mint a befejezési idő.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        datum: toLocalDateTimeString(datum),
        kezdesIdo,
        befejezesIdo,
        leiras: leiras.trim(),
      });
      onClose();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt a mentés során.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
            <DatePicker
              label="Dátum *"
              value={datum}
              onChange={(newValue) => setDatum(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Kezdési idő *"
              type="time"
              value={kezdesIdo}
              onChange={(event) => setKezdesIdo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
              required
            />
            <TextField
              label="Befejezési idő *"
              type="time"
              value={befejezesIdo}
              onChange={(event) => setBefejezesIdo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              fullWidth
              required
            />
          </Box>

          <TextField
            label="Leírás *"
            value={leiras}
            onChange={(event) => setLeiras(event.target.value)}
            fullWidth
            required
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Mégse</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Hozzáadás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNaptarBejegyzesDialog;
